import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Footer = memo(() => {
  const { t } = useLanguage();

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/technology', label: t('nav.technology') },
    { path: '/machinery', label: t('nav.machinery') },
    { path: '/career', label: t('nav.career') },
    { path: '/contact', label: t('nav.contact') }
  ];

  return (
    <footer data-testid="main-footer" className="bg-industrial-black border-t border-zinc-800">
      <div className="section-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <span className="font-heading font-bold text-3xl text-industrial-yellow tracking-wide">KAISRLÍK</span>
              <span className="text-lg text-gray-400 ml-3">spol. s r.o.</span>
            </div>
            <p className="text-gray-400 mb-6 max-w-md">
              {t('footer.company')}
            </p>
            <div className="space-y-3">
              <a 
                href="tel:+420352688045" 
                data-testid="footer-phone"
                className="flex items-center gap-3 text-gray-400 hover:text-industrial-yellow transition-colors"
              >
                <Phone size={16} />
                <span className="font-mono text-sm">+420 352 688 045</span>
              </a>
              <a 
                href="mailto:kaisrlik@kaisrlik.cz" 
                data-testid="footer-email"
                className="flex items-center gap-3 text-gray-400 hover:text-industrial-yellow transition-colors"
              >
                <Mail size={16} />
                <span className="font-mono text-sm">kaisrlik@kaisrlik.cz</span>
              </a>
              <div className="flex items-start gap-3 text-gray-400">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                <span className="font-mono text-sm">
                  Stannum 930<br />
                  357 31 Horní Slavkov<br />
                  Česká republika
                </span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-heading text-lg text-white mb-6 uppercase tracking-wider">
              Navigace
            </h4>
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`footer-nav-${link.path.replace('/', '') || 'home'}`}
                  className="text-gray-400 hover:text-industrial-yellow transition-colors text-sm"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Company Details */}
          <div>
            <h4 className="font-heading text-lg text-white mb-6 uppercase tracking-wider">
              Firemní údaje
            </h4>
            <div className="space-y-4">
              <div>
                <span className="text-gray-500 text-xs font-mono uppercase tracking-widest block mb-1">
                  {t('contact.info.ico')}
                </span>
                <span className="text-white font-mono">48362786</span>
              </div>
              <div>
                <span className="text-gray-500 text-xs font-mono uppercase tracking-widest block mb-1">
                  {t('contact.info.dic')}
                </span>
                <span className="text-white font-mono">CZ48362786</span>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-16 pt-8 border-t border-zinc-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm font-mono">
            © {new Date().getFullYear()} KAISRLÍK, spol. s r.o. — {t('footer.rights')}
          </p>
          <div className="flex items-center gap-4 text-gray-500 text-xs font-mono">
            <span>IČO: 48362786</span>
            <span className="text-zinc-700">|</span>
            <span>DIČ: CZ48362786</span>
          </div>
        </div>
      </div>
    </footer>
  );
});

export default Footer;
